"use client";

import React, { useState } from "react";
import { m, motion } from "framer-motion";

import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as ClockIcon,
  LocationOn as MapPinIcon,
} from "@mui/icons-material";
import emailjs from "emailjs-com";
import { globalTransition } from "../../assets/animate/globalAnimation";
import { c } from "framer-motion/dist/types.d-6pKw1mTI";

export default function CareerContactPage() {
  const [file, setFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    job: "",
    message: "",
    formType: "Career Form",
  });
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    job: "",
    message: "",
    file: "",
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      // Check if the file size is less than 1MB (1MB = 1,048,576 bytes)
      if (selectedFile.size > 1048576) {
        setError((prevErrors) => ({
          ...prevErrors,
          file: "File size must be less than 1MB",
        }));
        setFile(null); // Reset the file
        return;
      }

      setFile(selectedFile);
      setError((prevErrors) => ({
        ...prevErrors,
        file: "",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        file: "Please upload your CV",
      }));
    }
  };

  const validateForm = () => {
    const errors: {
      name: string;
      phone: string;
      email: string;
      address: string;
      job: string;
      message: string;
      file: string;
    } = {
      name: "",
      phone: "",
      email: "",
      address: "",
      job: "",
      message: "",
      file: "",
    };

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.phone.trim() && !formData.email.trim()) {
      errors.phone = "Either phone or email is required";
      errors.email = "Either phone or email is required";
    } else if (formData.phone.trim() && !/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone number must be 10 digits";
    } else if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      errors.email = "Invalid email format";
    }

    // if (!formData.address.trim()) {
    //   errors.address = "Address is required";
    // }

    if (!formData.job.trim()) {
      errors.job = "Current job is required";
    }

    if (!file) {
      errors.file = "Please upload your CV";
    }

    setError(errors);

    // Ensure either phone or email is provided
    const isContactInfoProvided =
      formData.phone.trim() || formData.email.trim();

    // Return true if there are no errors and contact info is provided
    return (
      Object.values(errors).every((err) => err === "") && isContactInfoProvided
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate the current field
    setError((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value),
    }));
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Name is required";
      case "phone":
        return ""; // No validation needed for phone if email is provided
      case "email":
        return ""; // No validation needed for email if phone is provided
      case "address":
        return "";
      case "job":
        return value.trim() ? "" : "Current job is required";
      case "message":
        return ""; // Message is optional
      default:
        return "";
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    setLoading(true); // Start the loader
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("job", formData.job);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("formType", formData.formType); // Include formType
      if (file) {
        formDataToSend.append("file", file);
      }
      const apiKey = process.env.REACT_APP_API_KEY_VALUE;
      const headers = {
        "x-api-key": apiKey as string,
       
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/send-job-application`,
        {
          method: "POST",
          headers: headers,
          body: formDataToSend,
        }
      );

      if (response.ok) {
        alert("Form submitted successfully!");
        // Reset the form
        setFormData({
          name: "",
          phone: "",
          email: "",
          address: "",
          job: "",
          message: "",
          formType: "Career Form", // Reset formType
        });
        setFile(null);
        setError({
          name: "",
          phone: "",
          email: "",
          address: "",
          job: "",
          message: "",
          file: "",
        });
      } else {
        const errorData = await response.json();
        console.error("Error sending email:", errorData.error);
        alert("Failed to submit the form: " + errorData.error);
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert("An error occurred while submitting the form.");
    } finally {
      setLoading(false); // Stop the loader
    }
  };

  return (
    <div className="h-[105rem]  md:h-[97rem] mdlg:h-[78rem] lg:h-[75rem] bg-[#3a4855] text-white mt-16 pt-16  !outoffice">
      {/* Career Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...globalTransition }}
          className="container mx-auto px-5"
        >
          <h2 className="lg:text-center text-start !text-4xl !md:text-5xl  !font-normal mb-2 lg:mb-7">
            Career
          </h2>

          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ ...globalTransition }}
            className="max-w-5xl text-start mx-auto lg:text-center mb-16"
          >
            <h2
              style={{ fontWeight: 300 }}
              className="lg:text-lg text-sm  leading-[2rem] text-white/80 pb-8 py-4"
            >
              We're always looking for talented, passionate people – both
              experienced professionals and recent graduates – across a wide
              range of disciplines. We want individuals who share our Core
              Values and who demonstrate a true passion for what they do,
              whether in the field or in the office. We value the richness that
              diversity brings to our workforce - it makes our Company better
              and the communities we serve stronger.
            </h2>
          </motion.div>

          <Grid container spacing={6} className="">
            {/* Form Section */}
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 2, md: 1 }}
              className="h-full"
            >
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ ...globalTransition }}
              >
                <div className="flex justify-center lg:justify-end z-50">
                  <Paper
                    elevation={3}
                    className="p-6 md:p-8 bg-white/10 backdrop-blur-sm rounded-lg  w-[30rem] xl:w-[25rem]"
                    style={{ zIndex: 10 }} // Apply z-index here
                  >
                    <form className="space-y-6 z-50 " onSubmit={handleSubmit}>
                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          className="mb-2  font-sans"
                          variant="body1"
                        >
                          First & Last Name
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.name}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.phone}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Email
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.email}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Your Address
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.address}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Current Job
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="job"
                          value={formData.job}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.job}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Message
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Type your message"
                          variant="outlined"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          InputProps={{
                            className: "bg-white/5 rounded-md text-white",
                          }}
                        />
                        <h2 className="text-red-400">{error?.message}</h2>
                      </div>

                      <div>
                        <Typography
                          sx={{ fontWeight: "bold", marginBottom: 1 }}
                          variant="subtitle1"
                          className="mb-2 "
                        >
                          Your CV
                        </Typography>
                        <Paper
                          elevation={0}
                          className="border border-dashed border-[#ab052d] rounded-md p-6 !bg-red-50 flex flex-col items-center justify-center cursor-pointer"
                        >
                          <input
                            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            className="hidden"
                            id="cv-upload"
                            type="file"
                            onChange={handleFileChange}
                          />
                          <label
                            htmlFor="cv-upload"
                            className="w-full cursor-pointer"
                          >
                            <div className="flex flex-col items-center">
                              <UploadIcon className="h-12 w-12 text-red-500 mb-2" />
                              <Typography className="text-center mb-2">
                                Drop file or upload
                              </Typography>
                              <Button
                                component="span"
                                className="!bg-[#ab052d] !text-white !text-xs normal-case"
                              >
                                Upload Files
                              </Button>
                            </div>
                          </label>
                        </Paper>
                      </div>
                      <h2 className="text-red-400">{error?.file}</h2>

                      <Button
                        fullWidth
                        size="large"
                        type="submit"
                        className={`!bg-[#ab052d] !text-white mt-4 py-4 ${
                          loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Send"}
                      </Button>
                    </form>
                  </Paper>
                </div>
              </motion.div>
            </Grid>

            {/* Contact Info Section */}
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ ...globalTransition }}
                className="h-full"
              >
                <Typography
                  variant="h4"
                  component="h2"
                  className="text-2xl md:text-3xl font-bold mb-8"
                >
                  Our Office
                </Typography>

                <div
                  className="space-y-8 font-sans text-white/80"
                  style={{ fontWeight: 200 }}
                >
                  <div className="flex items-start gap-4">
                    <MapPinIcon className="text-[#AB052D] h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <h2 className="text-lg">
                        L-4130/2, Phase IV, G.I.D.C.
                        <br />
                        B/H New Nirma
                        <br />
                        Vatva, Ahmedabad-382445.
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <EmailIcon className="text-[#AB052D] h-6 w-6 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <h2 className="text-lg">
                        <a
                          href="mailto:sales@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          sales@siskon.in
                        </a>
                      </h2>
                      <h2 className="text-lg">
                        <a
                          href="mailto:sanjay.jain@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          sanjay.jain@siskon.in
                        </a>
                      </h2>
                      <h2 className="text-lg">
                        <a
                          href="mailto:ishan.jain@siskon.in"
                          className="hover:underline"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          ishan.jain@siskon.in
                        </a>
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <PhoneIcon className="text-[#AB052D] h-6 w-6 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <h2 className="text-lg">
                        <a
                          href="tel:+919898507238"
                          className="hover:underline"
                          // style={{ color: "inherit", textDecoration: "none" }}
                        >
                          +91 9898507238
                        </a>
                      </h2>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <ClockIcon className="text-[#AB052D] h-6 w-6 mt-1 flex-shrink-0" />
                    <h2 className="text-lg">Mon – Sat: 9:00 AM – 6:00 PM</h2>
                  </div>
                </div>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </section>
    </div>
  );
}
